import type {
  ApiResponse,
  PaginatedResponse,
  PropertyListItemResDTO,
  PropertyDetailResDTO,
  PropertyBasicUpdateReqDTO,
  ContactInfoEmailDTO,
  BookRequestEmailDTO,
  ContactInfoResDTO,
  ContactInfoUpdateReqDTO,
  AmenityResDTO,
  AmenityCreateReqDTO,
  AmenityUpdateReqDTO,
  AmenityQueryParams,
  FeatureResDTO,
  FeatureCreateReqDTO,
  FeatureUpdateReqDTO,
  FeatureQueryParams,
  RuleResDTO,
  RuleCreateReqDTO,
  RuleUpdateReqDTO,
  RuleQueryParams,
  FaqResDTO,
  FaqCreateReqDTO,
  FaqUpdateReqDTO,
  FaqQueryParams,
  CityResDTO,
  PropertyQueryParams,
} from "@/types/api"

// When code runs in the browser we must stay in-origin (=> /api/**).
// On the server we can talk directly to Azure.
const API_BASE_URL = typeof window === "undefined" 
  ? process.env.NEXT_PUBLIC_API_BASE_URL || "https://raimerentalsapi.azurewebsites.net/api"
  : "/api"

  
// Generic API client
class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  // Get auth token from localStorage
  private getAuthToken(): string | null {
    if (typeof window === "undefined") return null
    return localStorage.getItem("auth_token")
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    // Get auth token and add to headers if available
    const token = this.getAuthToken()
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {}

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)

      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Clear auth data and redirect to login
        if (typeof window !== "undefined") {
          localStorage.removeItem("auth_token")
          localStorage.removeItem("auth_user")
          window.location.href = "/auth"
        }
        throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.")
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const apiResponse: ApiResponse<T> = await response.json()

      if (!apiResponse.success) {
        throw new Error(apiResponse.errorMessage || "API request failed")
      }

      return apiResponse.result
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }

      throw new Error(error instanceof Error ? error.message : "An unknown error occurred")
    }
  }

  // FormData request method
  private async requestFormData<T>(endpoint: string, formData: FormData, method = "POST"): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    // Get auth token and add to headers if available
    const token = this.getAuthToken()
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {}

    const config: RequestInit = {
      method,
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...authHeaders,
      },
      body: formData,
    }

    try {
      const response = await fetch(url, config)

      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Clear auth data and redirect to login
        if (typeof window !== "undefined") {
          localStorage.removeItem("auth_token")
          localStorage.removeItem("auth_user")
          window.location.href = "/auth"
        }
        throw new Error("Sesión expirada. Por favor, inicia sesión nuevamente.")
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
      }

      const apiResponse: ApiResponse<T> = await response.json()

      if (!apiResponse.success) {
        throw new Error(apiResponse.errorMessage || "API request failed")
      }

      return apiResponse.result
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }

      throw new Error(error instanceof Error ? error.message : "An unknown error occurred")
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const searchParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.append(key, String(value))
        }
      })
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint

    return this.request<T>(url)
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.requestFormData<T>(endpoint, formData, "POST")
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async putFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.requestFormData<T>(endpoint, formData, "PUT")
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    })
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL)

// Property API service
export const propertyService = {
  // Get paginated properties
  async getProperties(params: PropertyQueryParams = {}): Promise<PaginatedResponse<PropertyListItemResDTO>> {
    return apiClient.get<PaginatedResponse<PropertyListItemResDTO>>("/property", params)
  },

  // Get featured properties
  async getFeaturedProperties(pageSize = 10): Promise<PaginatedResponse<PropertyListItemResDTO>> {
    return this.getProperties({
      pageIndex: 1,
      pageSize,
      isFeatured: true,
    })
  },

  // Get property by ID (UPDATED for detail response)
  async getPropertyById(id: number): Promise<PropertyDetailResDTO> {
    return apiClient.get<PropertyDetailResDTO>(`/property/${id}`)
  },

  // Basic update property using FormData (UPDATED)
  async updatePropertyBasic(id: number, data: PropertyBasicUpdateReqDTO): Promise<PropertyDetailResDTO> {
    const formData = new FormData()
    formData.append("Name", data.name)
    formData.append("Price", data.price.toString())
    formData.append("Bathrooms", data.bathrooms.toString())
    formData.append("Roommates", data.roommates.toString())
    formData.append("Diningrooms", data.diningrooms.toString())
    formData.append("IsFeatured", data.isFeatured.toString())

    return apiClient.putFormData<PropertyDetailResDTO>(`/property/${id}/basic-update`, formData)
  },

  // Search properties with filters
  async searchProperties(params: PropertyQueryParams): Promise<PaginatedResponse<PropertyListItemResDTO>> {
    return this.getProperties(params)
  },
}

// City API service - UPDATED with CRUD operations
export const cityService = {
  // Get all cities
  async getCities(): Promise<CityResDTO[]> {
    return apiClient.get<CityResDTO[]>("/city")
  },

  // Get city by ID
  async getCityById(id: number): Promise<CityResDTO> {
    return apiClient.get<CityResDTO>(`/city/${id}`)
  },

  // Create city
  async createCity(name: string): Promise<CityResDTO> {
    return apiClient.post<CityResDTO>("/city", { name })
  },

  // Update city
  async updateCity(id: number, name: string): Promise<CityResDTO> {
    return apiClient.put<CityResDTO>(`/city/${id}`, { name })
  },
}

// Email API service (NEW)
export const emailService = {
  // Send contact info email
  async sendContactInfo(data: ContactInfoEmailDTO): Promise<void> {
    return apiClient.post<void>("/mail/info", data)
  },

  // Send book request email
  async sendBookRequest(data: BookRequestEmailDTO): Promise<void> {
    return apiClient.post<void>("/mail/book-request", data)
  },
}

// Amenity API service
export const amenityService = {
  // Get paginated amenities
  async getAmenities(params: AmenityQueryParams = {}): Promise<PaginatedResponse<AmenityResDTO>> {
    return apiClient.get<PaginatedResponse<AmenityResDTO>>("/amenities", params)
  },

  // Get amenity by ID
  async getAmenityById(id: number): Promise<AmenityResDTO> {
    return apiClient.get<AmenityResDTO>(`/amenities/${id}`)
  },

  // Create amenity
  async createAmenity(data: AmenityCreateReqDTO): Promise<AmenityResDTO> {
    return apiClient.post<AmenityResDTO>("/amenities", data)
  },

  // Update amenity
  async updateAmenity(id: number, data: AmenityUpdateReqDTO): Promise<AmenityResDTO> {
    return apiClient.put<AmenityResDTO>(`/amenities/${id}`, data)
  },

  // Delete amenity
  async deleteAmenity(id: number): Promise<void> {
    return apiClient.delete<void>(`/amenities/${id}`)
  },
}

// Feature API service
export const featureService = {
  // Get paginated features
  async getFeatures(params: FeatureQueryParams = {}): Promise<PaginatedResponse<FeatureResDTO>> {
    return apiClient.get<PaginatedResponse<FeatureResDTO>>("/features", params)
  },

  // Get feature by ID
  async getFeatureById(id: number): Promise<FeatureResDTO> {
    return apiClient.get<FeatureResDTO>(`/features/${id}`)
  },

  // Create feature
  async createFeature(data: FeatureCreateReqDTO): Promise<FeatureResDTO> {
    return apiClient.post<FeatureResDTO>("/features", data)
  },

  // Update feature
  async updateFeature(id: number, data: FeatureUpdateReqDTO): Promise<FeatureResDTO> {
    return apiClient.put<FeatureResDTO>(`/features/${id}`, data)
  },

  // Delete feature
  async deleteFeature(id: number): Promise<void> {
    return apiClient.delete<void>(`/features/${id}`)
  },
}

// Rule API service
export const ruleService = {
  // Get paginated rules
  async getRules(params: RuleQueryParams = {}): Promise<PaginatedResponse<RuleResDTO>> {
    return apiClient.get<PaginatedResponse<RuleResDTO>>("/rules", params)
  },

  // Get rule by ID
  async getRuleById(id: number): Promise<RuleResDTO> {
    return apiClient.get<RuleResDTO>(`/rules/${id}`)
  },

  // Create rule
  async createRule(data: RuleCreateReqDTO): Promise<RuleResDTO> {
    return apiClient.post<RuleResDTO>("/rules", data)
  },

  // Update rule
  async updateRule(id: number, data: RuleUpdateReqDTO): Promise<RuleResDTO> {
    return apiClient.put<RuleResDTO>(`/rules/${id}`, data)
  },

  // Delete rule
  async deleteRule(id: number): Promise<void> {
    return apiClient.delete<void>(`/rules/${id}`)
  },
}

// FAQ API service
export const faqService = {
  // Get paginated faqs
  async getFaqs(params: FaqQueryParams = {}): Promise<PaginatedResponse<FaqResDTO>> {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<FaqResDTO>>>("/faqs", params)
    
    // Handle case where response might already be unwrapped or differently structured
    if (response.result) {
      return response.result
    } else if (response.items) {
      // If response is already unwrapped
      return response as any
    } else {
      throw new Error("Unexpected FAQ API response structure")
    }
  },

  // Get faq by ID
  async getFaqById(id: number): Promise<FaqResDTO> {
    return apiClient.get<FaqResDTO>(`/faqs/${id}`)
  },

  // Create faq
  async createFaq(data: FaqCreateReqDTO): Promise<FaqResDTO> {
    return apiClient.post<FaqResDTO>("/faqs", data)
  },

  // Update faq
  async updateFaq(id: number, data: FaqUpdateReqDTO): Promise<FaqResDTO> {
    return apiClient.put<FaqResDTO>(`/faqs/${id}`, data)
  },

  // Delete faq
  async deleteFaq(id: number): Promise<void> {
    return apiClient.delete<void>(`/faqs/${id}`)
  },
}

// Contact Info API service
export const contactInfoService = {
  // Get contact info
  async getContactInfo(): Promise<ContactInfoResDTO> {
    return apiClient.get<ContactInfoResDTO>("/generalparams/contactinfo")
  },

  // Update contact info
  async updateContactInfo(data: ContactInfoUpdateReqDTO): Promise<ContactInfoResDTO> {
    return apiClient.put<ContactInfoResDTO>("/generalparams/contactinfo", data)
  },
}

// Export API client for other services
export { apiClient }
